import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Package, Upload, Loader2, User, BarChart3, Download, FileSpreadsheet, CreditCard } from "lucide-react";
import { api, API } from "@/lib/api";
import { authFetch, useAuth, getToken } from "@/lib/auth";
import PartCard from "@/components/PartCard";
import BillingTab from "@/components/BillingTab";
import UpgradeModal from "@/components/UpgradeModal";

const CATEGORIES = ["Engine", "Drivetrain", "Brakes", "Electrical", "Cooling", "Body", "Suspension", "Fuel System", "Accessories", "Interior", "Wheels & Tyres", "4x4 & Off-Road", "Glass", "Other"];
const CONDITIONS = [{ v: "new", l: "New" }, { v: "used", l: "Used" }, { v: "refurbished", l: "Refurbished" }];
const CITIES = ["Johannesburg", "Cape Town", "Durban", "Pretoria", "Port Elizabeth", "Bloemfontein", "East London", "Polokwane", "Nelspruit", "Kimberley"];
const TYPES = [
  { v: "scrapyard", label: "Scrapyard" },
  { v: "dealership", label: "Dealership" },
  { v: "aftermarket", label: "Aftermarket" },
  { v: "workshop", label: "Workshop" },
  { v: "accessories", label: "Vehicle Accessories" },
  { v: "interior", label: "Interior" },
  { v: "wheels_tyres", label: "Wheels & Tyres" },
  { v: "offroad", label: "4x4 & Off-Road" },
  { v: "glass", label: "Glass & Visibility" },
];

const empty = {
  name: "", category: "Engine", make: "", model: "", year_from: 2010, year_to: 2024,
  engine: "", variant: "", oem_number: "", condition: "new", price_zar: 0,
  warranty_months: 0, compatibility_notes: "",
};

export default function SupplierDashboardPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [supplier, setSupplier] = useState(null);
  const [parts, setParts] = useState([]);
  const [tab, setTab] = useState("inventory"); // inventory | profile | analytics
  const isOwner = user && user.supplier_id === id;

  // If a supplier visits another supplier's dashboard URL, redirect to their own.
  // The backend already 401/403s write ops, but this prevents UX confusion.
  useEffect(() => {
    if (user && user.supplier_id && user.supplier_id !== id) {
      window.location.replace(`/suppliers/dashboard/${user.supplier_id}`);
    }
  }, [user, id]);

  const load = async () => {
    try {
      const [s, p] = await Promise.all([
        api.get(`/suppliers/${id}`),
        api.get(`/suppliers/${id}/parts`),
      ]);
      setSupplier(s.data);
      setParts(p.data);
    } catch (e) {
      toast.error("Could not load supplier");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!supplier) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 text-sm text-neutral-500">
        Loading...
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <div className="text-xs font-mono-tech tracking-[0.25em] text-[#FF5100] mb-2">
        SUPPLIER DASHBOARD {!isOwner && "· READ-ONLY"}
      </div>
      <h1 data-testid="supplier-dashboard-title" className="font-display text-4xl sm:text-5xl font-extrabold leading-none text-neutral-900">
        {supplier.name}
      </h1>
      <div className="mt-2 text-sm text-neutral-600">
        {supplier.city} · {supplier.type} · {parts.length} parts listed
      </div>

      {/* Tabs */}
      <div className="mt-6 flex gap-1 border-b border-neutral-200">
        <Tab id="inventory" label="Inventory" icon={<Package size={14} />} active={tab === "inventory"} onClick={setTab} />
        {isOwner && <Tab id="profile" label="Profile" icon={<User size={14} />} active={tab === "profile"} onClick={setTab} />}
        {isOwner && <Tab id="analytics" label="Analytics" icon={<BarChart3 size={14} />} active={tab === "analytics"} onClick={setTab} />}
        {isOwner && <Tab id="billing" label="Billing" icon={<CreditCard size={14} />} active={tab === "billing"} onClick={setTab} />}
      </div>

      <div className="mt-6">
        {tab === "inventory" && (
          <InventoryTab supplier={supplier} parts={parts} isOwner={isOwner} onChange={load} />
        )}
        {tab === "profile" && isOwner && (
          <ProfileTab supplier={supplier} onSaved={load} />
        )}
        {tab === "analytics" && isOwner && (
          <AnalyticsTab supplierId={id} />
        )}
        {tab === "billing" && isOwner && (
          <BillingTab />
        )}
      </div>
    </div>
  );
}

function Tab({ id, label, icon, active, onClick }) {
  return (
    <button
      type="button"
      data-testid={`tab-${id}`}
      onClick={() => onClick(id)}
      className={`flex items-center gap-1.5 px-4 h-10 text-xs uppercase tracking-wider font-bold border-b-2 -mb-px transition-colors ${
        active ? "border-[#FF5100] text-[#FF5100]" : "border-transparent text-neutral-600 hover:text-neutral-900"
      }`}
    >
      {icon} {label}
    </button>
  );
}

/* ────────────── Inventory tab ────────────── */
function InventoryTab({ supplier, parts, isOwner, onChange }) {
  const [form, setForm] = useState(empty);
  const [adding, setAdding] = useState(false);
  const [pendingImages, setPendingImages] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [upgradePrompt, setUpgradePrompt] = useState(null);  // {reason, message} or null
  const fileInputRef = useRef(null);
  const csvInputRef = useRef(null);

  const handle402 = (err) => {
    const status = err.response?.status;
    const detail = err.response?.data?.detail || err.message;
    if (status === 402) {
      const reason = /trial|past due|cancelled/i.test(detail) ? "past_due" : "limit";
      setUpgradePrompt({ reason, message: detail });
      return true;
    }
    return false;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.make || !form.model || !form.engine) {
      toast.error("Name, make, model, and engine are required");
      return;
    }
    setAdding(true);
    try {
      const { data: created } = await api.post(`/suppliers/${supplier.id}/parts`, {
        ...form,
        year_from: Number(form.year_from),
        year_to: Number(form.year_to),
        price_zar: Number(form.price_zar),
        warranty_months: Number(form.warranty_months),
      }, authFetch());
      for (const file of pendingImages) {
        const fd = new FormData();
        fd.append("file", file);
        await fetch(`${API}/parts/${created.id}/images`, {
          method: "POST",
          headers: { Authorization: `Bearer ${getToken()}` },
          body: fd,
        });
      }
      toast.success("Part listed");
      setForm(empty);
      setPendingImages([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      onChange();
    } catch (e) {
      if (!handle402(e)) {
        toast.error(e.response?.data?.detail || "Could not list part");
      }
    } finally {
      setAdding(false);
    }
  };

  const onCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/suppliers/${supplier.id}/parts/bulk`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 402) {
          const reason = /trial|past due|cancelled/i.test(data.detail || "") ? "past_due" : "limit";
          setUpgradePrompt({ reason, message: data.detail });
          return;
        }
        throw new Error(data.detail || "Upload failed");
      }
      if (data.skipped_due_to_limit > 0) {
        toast.warning(`Created ${data.created} parts. ${data.skipped_due_to_limit} skipped — plan limit reached.`);
        setUpgradePrompt({ reason: "limit", message: `${data.skipped_due_to_limit} rows skipped because you reached your plan listing limit.` });
      } else if (data.error_count > 0) {
        toast.warning(`Created ${data.created} parts, ${data.error_count} rows had errors`);
      } else {
        toast.success(`Imported ${data.created} parts`);
      }
      onChange();
    } catch (err) {
      toast.error(err.message || "CSV upload failed");
    } finally {
      setBulkBusy(false);
      if (csvInputRef.current) csvInputRef.current.value = "";
    }
  };

  return (
    <div className="grid lg:grid-cols-[1fr,1.4fr] gap-8">
      {upgradePrompt && (
        <UpgradeModal
          onClose={() => setUpgradePrompt(null)}
          reason={upgradePrompt.reason}
          message={upgradePrompt.message}
        />
      )}
      {isOwner ? (
        <div className="space-y-4 self-start">
          <form onSubmit={submit} data-testid="part-form" className="border border-neutral-200 bg-neutral-50 p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Plus size={16} className="text-[#FF5100]" />
              <h2 className="font-display text-2xl font-bold text-neutral-900">List a part</h2>
            </div>
            <Inp label="Part name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="part-name" placeholder="e.g. Starter Motor" />
            <Row cols={2}>
              <Sel label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={CATEGORIES} testid="part-category" />
              <Sel label="Condition" value={form.condition} onChange={(v) => setForm({ ...form, condition: v })} options={CONDITIONS.map(c => c.v)} labels={CONDITIONS.map(c => c.l)} testid="part-condition" />
            </Row>
            <Row cols={2}>
              <Inp label="Make *" value={form.make} onChange={(v) => setForm({ ...form, make: v })} testid="part-make" placeholder="Toyota" />
              <Inp label="Model *" value={form.model} onChange={(v) => setForm({ ...form, model: v })} testid="part-model" placeholder="Hilux" />
            </Row>
            <Row cols={2}>
              <Inp label="Year from" type="number" value={form.year_from} onChange={(v) => setForm({ ...form, year_from: v })} testid="part-year-from" />
              <Inp label="Year to" type="number" value={form.year_to} onChange={(v) => setForm({ ...form, year_to: v })} testid="part-year-to" />
            </Row>
            <Row cols={2}>
              <Inp label="Engine *" value={form.engine} onChange={(v) => setForm({ ...form, engine: v })} testid="part-engine" placeholder="GD6" />
              <Inp label="Variant" value={form.variant} onChange={(v) => setForm({ ...form, variant: v })} testid="part-variant" placeholder="2.8 GD6" />
            </Row>
            <Inp label="OEM #" value={form.oem_number} onChange={(v) => setForm({ ...form, oem_number: v })} testid="part-oem" placeholder="28100-0E020" mono />
            <Row cols={2}>
              <Inp label="Price (ZAR)" type="number" value={form.price_zar} onChange={(v) => setForm({ ...form, price_zar: v })} testid="part-price" />
              <Inp label="Warranty (months)" type="number" value={form.warranty_months} onChange={(v) => setForm({ ...form, warranty_months: v })} testid="part-warranty" />
            </Row>
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500 mb-1.5">Compatibility notes</div>
              <textarea
                data-testid="part-notes"
                value={form.compatibility_notes}
                onChange={(e) => setForm({ ...form, compatibility_notes: e.target.value })}
                rows={2}
                className="w-full bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-3 py-2 text-sm"
                placeholder="Anything mechanics should know..."
              />
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500 mb-1.5">Photos (up to 8MB each)</div>
              <div className="flex items-center gap-2">
                <label className="cursor-pointer flex items-center gap-2 px-3 h-11 border border-neutral-200 hover:border-neutral-300 text-xs uppercase tracking-wider transition-colors">
                  <Upload size={14} />
                  Choose files
                  <input
                    ref={fileInputRef}
                    data-testid="part-image-input"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    onChange={(e) => setPendingImages(Array.from(e.target.files || []))}
                    className="hidden"
                  />
                </label>
                {pendingImages.length > 0 && (
                  <span className="text-xs text-neutral-600">{pendingImages.length} ready</span>
                )}
              </div>
            </label>
            <button
              type="submit"
              data-testid="part-submit-button"
              disabled={adding}
              className="w-full px-6 h-11 bg-[#FF5100] hover:bg-[#E64A00] disabled:opacity-50 text-white font-bold uppercase tracking-wider text-xs transition-colors flex items-center justify-center gap-2"
            >
              {adding ? <><Loader2 size={14} className="animate-spin" /> Adding...</> : "Publish Listing"}
            </button>
          </form>

          {/* Bulk CSV uploader */}
          <div className="border border-neutral-200 bg-neutral-50 p-5 space-y-3">
            <div className="flex items-center gap-2">
              <FileSpreadsheet size={16} className="text-[#FF5100]" />
              <h2 className="font-display text-xl font-bold text-neutral-900">Bulk upload</h2>
            </div>
            <p className="text-xs text-neutral-600">
              Got a spreadsheet with your stock? Upload it here. Required columns: <span className="font-mono-tech">name, make, model, engine, price_zar</span>. New to this? Grab the <span className="font-bold">Excel</span> template — it has dropdowns and an Instructions sheet.
            </p>
            <div className="flex flex-wrap gap-2">
              <a
                data-testid="xlsx-template-link"
                href="/api/parts/bulk/template.xlsx"
                className="flex items-center gap-1.5 px-3 h-9 border border-[#FF5100] bg-white text-[#FF5100] hover:bg-[#FFF6F0] text-xs uppercase tracking-wider transition-colors font-bold"
              >
                <Download size={12} /> Excel template
              </a>
              <a
                data-testid="csv-template-link"
                href="/api/parts/bulk/template.csv"
                className="flex items-center gap-1.5 px-3 h-9 border border-neutral-200 hover:border-neutral-300 text-xs uppercase tracking-wider transition-colors"
              >
                <Download size={12} /> CSV template
              </a>
              <label className="cursor-pointer flex items-center gap-1.5 px-3 h-9 bg-[#FF5100] hover:bg-[#E64A00] text-white font-bold text-xs uppercase tracking-wider transition-colors">
                {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                Upload file
                <input
                  ref={csvInputRef}
                  data-testid="csv-upload-input"
                  type="file"
                  accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={onCsv}
                  className="hidden"
                  disabled={bulkBusy}
                />
              </label>
            </div>
          </div>
        </div>
      ) : (
        <div className="border border-neutral-200 bg-neutral-50 p-5 self-start text-sm text-neutral-600">
          Sign in as the owner of this account to list parts.
        </div>
      )}

      <div>
        <div className="flex items-center gap-2 mb-4">
          <Package size={16} className="text-neutral-600" />
          <h2 className="font-display text-2xl font-bold text-neutral-900">Inventory</h2>
        </div>
        {parts.length === 0 ? (
          <div data-testid="empty-inventory" className="border border-neutral-200 p-8 text-center text-sm text-neutral-600">
            No parts listed yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {parts.map((p, i) => (
              <PartCard key={p.id} part={p} index={i} owned={isOwner} onChange={onChange} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ────────────── Profile tab ────────────── */
function ProfileTab({ supplier, onSaved }) {
  const [form, setForm] = useState({
    name: supplier.name,
    city: supplier.city,
    type: supplier.type,
    phone: supplier.phone || "",
    email: supplier.email || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/suppliers/${supplier.id}`, form, authFetch());
      toast.success("Profile updated");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} data-testid="profile-form" className="max-w-xl border border-neutral-200 bg-neutral-50 p-6 space-y-4">
      <h2 className="font-display text-2xl font-bold text-neutral-900">Business profile</h2>
      <p className="text-xs text-neutral-600">
        Mechanics see these details on your supplier card. Changes apply to all your listings instantly.
      </p>
      <Inp label="Business name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="profile-name" />
      <Row cols={2}>
        <Sel label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} options={CITIES} testid="profile-city" />
        <Sel label="Type" value={form.type} onChange={(v) => setForm({ ...form, type: v })} options={TYPES.map(t => t.v)} labels={TYPES.map(t => t.label)} testid="profile-type" />
      </Row>
      <Row cols={2}>
        <Inp label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="profile-phone" placeholder="+27 ..." />
        <Inp label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="profile-email" placeholder="sales@yourcompany.co.za" />
      </Row>
      <button
        type="submit"
        data-testid="profile-save"
        disabled={busy}
        className="px-6 h-11 bg-[#FF5100] hover:bg-[#E64A00] disabled:opacity-50 text-white font-bold uppercase tracking-wider text-xs transition-colors"
      >
        {busy ? "Saving..." : "Save Profile"}
      </button>
    </form>
  );
}

/* ────────────── Analytics tab ────────────── */
function AnalyticsTab({ supplierId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    api.get(`/suppliers/${supplierId}/analytics`, authFetch())
      .then((r) => { if (live) setData(r.data); })
      .catch(() => toast.error("Could not load analytics"))
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [supplierId]);

  if (loading) return <div className="text-sm text-neutral-500 font-mono-tech">[ Loading analytics... ]</div>;
  if (!data) return null;

  return (
    <div data-testid="analytics-tab" className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Parts listed" value={data.totals.parts} />
        <Stat label="Search views" value={data.totals.views} hint="Times your parts surfaced in mechanic searches" />
        <Stat label="Quote requests" value={data.totals.quotes} hint="Times mechanics submitted the contact form" />
        <Stat label="WhatsApp YES" value={data.totals.wa_connections} hint="Mechanics who confirmed via WhatsApp" />
      </div>

      <div>
        <h3 className="font-display text-xl font-bold text-neutral-900 mb-3">Top-performing parts</h3>
        {data.top_parts.length === 0 ? (
          <div className="border border-neutral-200 p-6 text-sm text-neutral-600">
            No views yet. Once mechanics start searching, your top performers will appear here.
          </div>
        ) : (
          <div className="border border-neutral-200">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-600">Part</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-600">Vehicle</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-600">Price</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-600">Views</th>
                </tr>
              </thead>
              <tbody>
                {data.top_parts.map((p) => (
                  <tr key={p.part_id} className="border-b border-neutral-100">
                    <td className="px-3 py-2 font-medium">{p.name}</td>
                    <td className="px-3 py-2 text-neutral-600">{p.make} {p.model}</td>
                    <td className="px-3 py-2 text-right font-mono-tech">R{Math.round(p.price_zar).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-bold text-[#FF5100]">{p.views}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div className="border border-neutral-200 bg-neutral-50 p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-600">{label}</div>
      <div className="font-display text-4xl font-extrabold text-neutral-900 mt-1">{value}</div>
      {hint && <div className="text-[11px] text-neutral-500 mt-1 leading-tight">{hint}</div>}
    </div>
  );
}

/* ────────────── shared form helpers ────────────── */
const Row = ({ children, cols = 1 }) => (
  <div className={`grid gap-4 ${cols === 2 ? "grid-cols-2" : "grid-cols-1"}`}>{children}</div>
);

function Inp({ label, value, onChange, type = "text", testid, placeholder, mono }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500 mb-1.5">{label}</div>
      <input
        data-testid={testid}
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-3 h-11 text-sm ${mono ? "font-mono-tech" : ""}`}
      />
    </label>
  );
}

function Sel({ label, value, onChange, options, labels, testid }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500 mb-1.5">{label}</div>
      <select
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-3 h-11 text-sm"
      >
        {options.map((o, i) => <option key={o} value={o}>{labels?.[i] ?? o}</option>)}
      </select>
    </label>
  );
}
